// Versioned localStorage store for the static build. The ONLY durable user data is
// vocab/SRS (the words they know + review schedule); reading position is minor; glosses
// are a personal extra. Content updates (timings/stories) ship in the bundle and never
// touch this store. The schema is VERSIONED from day one so the rare shape change migrates
// cleanly — back up first, idempotent steps — and known words are never lost.
//
// Keys (origin-scoped; stable forever on seediq-reader.netlify.app):
//   seediq_schema_version   "1"
//   seediq_vocab_v1         { "<dialect>:<word>": { status, ...SM-2 fields } }   ← the precious one
//   seediq_progress_v1      { textId: itemIndex }
//   seediq_gloss_v1         { "<dialect>:<word>": { gloss, was, at, note } }  ← the expert's
//   seediq_lastroute_v1     "/tao/"   ← the address to reopen on a bare visit
//
// A gloss used to be a bare string, and that threw away the two things that
// make a correction worth having. `was` is what the dictionary said at the
// moment it was overwritten, so "" means they filled a blank and a non-empty
// string means they disagreed with a published gloss — and only the second is
// news. `note` is why. A speaker correcting their own language usually has a
// reason ("right in Tgdaya, not Toda") that a replacement string cannot carry.

const SCHEMA_KEY = 'seediq_schema_version';
const SCHEMA_VERSION = 3;

export const VOCAB_KEY = 'seediq_vocab_v1';
export const PROGRESS_KEY = 'seediq_progress_v1';
export const GLOSS_KEY = 'seediq_gloss_v1';
export const ROUTE_KEY = 'seediq_lastroute_v1';

function read(key, fallback) {
  try { const v = localStorage.getItem(key); return v == null ? fallback : JSON.parse(v); }
  catch { return fallback; }
}
function write(key, val) {
  try { localStorage.setItem(key, JSON.stringify(val)); } catch { /* quota / private mode */ }
}

// ── migration ──────────────────────────────────────────────────────────────
// Runs once at module load. Bumps the stored schema to SCHEMA_VERSION via ordered,
// idempotent steps. Always snapshots the current data first so a bad migration is
// recoverable. Add `if (v < N) { ...; v = N; }` blocks for future shape changes.
function migrate() {
  let v = parseInt(localStorage.getItem(SCHEMA_KEY) || '0', 10) || 0;
  if (v === SCHEMA_VERSION) return;

  try {
    const snap = {};
    for (const k of [VOCAB_KEY, PROGRESS_KEY, GLOSS_KEY]) {
      const raw = localStorage.getItem(k);
      if (raw != null) snap[k] = raw;
    }
    if (Object.keys(snap).length) {
      localStorage.setItem(`seediq_premigrate_${Date.now()}`, JSON.stringify(snap));
    }
  } catch { /* best effort */ }

  if (v < 1) {
    // 0 -> 1: adopt the v1 keys. Rescue any pre-static localStorage left by the
    // old backend-era build ('seediq_vocab') so early testers keep their words.
    if (localStorage.getItem(VOCAB_KEY) == null) {
      const legacy = read('seediq_vocab', null);
      if (legacy && typeof legacy === 'object') write(VOCAB_KEY, legacy);
    }
    v = 1;
  }
  if (v < 2) {
    // 1 -> 2: glosses become records. Any bare string here predates both the
    // language-keyed key and the record, so its `was` and `at` are gone for
    // good — recorded as null rather than guessed. There are provably none of
    // these in the cloud (no shipped build had a gloss editor); this exists for
    // dev browsers, and so getAll() has exactly one shape to reason about.
    const g = read(GLOSS_KEY, null);
    if (g && typeof g === 'object') {
      let touched = false;
      for (const [k, val] of Object.entries(g)) {
        if (typeof val === 'string') { g[k] = { gloss: val, was: null, at: null }; touched = true; }
      }
      if (touched) write(GLOSS_KEY, g);
    }
    v = 2;
  }
  if (v < 3) {
    // 2 -> 3: drop the phantom corrections the gloss editor made by accident.
    // Opening the edit box PREFILLED the dictionary's answer, so 儲存 with
    // nothing typed filed the whole aggregated e-dict entry as a personal
    // correction. fed69f4 stopped new ones; the stored ones outlive it, and a
    // user gloss SHADOWS the dictionary (WordPopup's `userGloss ?? omniGloss`),
    // so the word is frozen on whatever the dictionary said that day and every
    // later fix to it is invisible. yami 'ya' was pinned to 他；它 that way
    // after its entry had been rewritten to the aspect marker.
    //
    // gloss === was is exactly and only that no-op: a real correction differs
    // from the dictionary answer it replaced, or it would not be a correction.
    // A note means they meant it, whatever the strings say. Everything else is
    // left alone -- this deletes, so it errs toward keeping.
    const g = read(GLOSS_KEY, null);
    if (g && typeof g === 'object') {
      let touched = false;
      for (const [k, r] of Object.entries(g)) {
        if (r && typeof r === 'object' && !r.note &&
            typeof r.was === 'string' && r.gloss === r.was) { delete g[k]; touched = true; }
      }
      if (touched) write(GLOSS_KEY, g);
    }
    v = 3;
  }
  // future: if (v < 4) { ...; v = 4; }

  localStorage.setItem(SCHEMA_KEY, String(SCHEMA_VERSION));
}
migrate();

// ── vocab (status + SRS) ─────────────────────────────────────────────────────
export const vocabStore = {
  getAll: () => read(VOCAB_KEY, {}),
  // Merge a patch into one "dialect:word" entry; returns the new full map.
  // Stamps updatedAt so vocabSync.js's cloud merge can tell cards apart.
  setKey: (key, patch) => {
    const all = read(VOCAB_KEY, {});
    all[key] = { ...all[key], ...patch, updatedAt: new Date().toISOString() };
    write(VOCAB_KEY, all);
    return all;
  },
  // Full-replace with a cloud-merged map (vocabSync.js pull/push response).
  replaceKeys: (entries) => {
    write(VOCAB_KEY, entries);
    return entries;
  },
};

// ── reading position ─────────────────────────────────────────────────────────
export const progressStore = {
  getAll: () => read(PROGRESS_KEY, {}),
  set: (textId, index) => {
    const all = read(PROGRESS_KEY, {});
    all[textId] = index;
    write(PROGRESS_KEY, all);
    return all;
  },
};

// ── gloss corrections ────────────────────────────────────────────────────────

// The one place that knows a gloss entry may be a record OR a bare string.
// Everything that displays a gloss goes through here, so the migration above
// is a tidiness measure and not the thing correctness rests on: a string that
// slips in from the dev backend (which stores bare strings) still renders.
export function glossText(entry) {
  if (entry == null) return null;
  return typeof entry === 'string' ? entry : (entry.gloss ?? null);
}

export const glossStore = {
  getAll: () => read(GLOSS_KEY, {}),
  // Takes the finished "<dialect>:<word>" key — built by entryKey, the same
  // builder vocab uses. It used to take a bare word and lowercase it here,
  // which is how glosses ended up language-blind in sixteen languages that
  // share one orthography.
  set: (key, record) => {
    const all = read(GLOSS_KEY, {});
    if (record == null || !record.gloss) delete all[key]; else all[key] = record;
    write(GLOSS_KEY, all);
    return all;
  },
};

// ── last address ─────────────────────────────────────────────────────
// Which screen to reopen when someone types the bare domain. Deliberately NOT in
// cloudSync's BACKUP_KEYS: reading position is worth carrying to a second device,
// but "the book my laptop had open" is not — a restored phone should still land
// on the shelf, not somewhere its owner never was.
export const routeStore = {
  get: () => { try { return localStorage.getItem(ROUTE_KEY) || ''; } catch { return ''; } },
  set: (path) => { try { localStorage.setItem(ROUTE_KEY, path); } catch { /* quota / private mode */ } },
};
